/**
 * Kanalmodellen for Chat-rommet (/chat, VIB-3022).
 *
 * To kilder blir én liste:
 * - rom fra `chat_rooms` (husrom, grupper og direktemeldinger)
 * - prosjektkanaler, som ikke er rader i `chat_rooms`, men prosjekter der
 *   brukeren er med i teamet. Meldingene der er `project_comments`.
 *
 * Kanalnøkkelen (`room:<id>` / `project:<id>`) er det som står i URL-en, så
 * «Åpne i Chat» fra prosjektkortet kan lenke rett til kanalen.
 */

export type ChannelKind = "project" | "house" | "group" | "direct";

export type ChannelGroupId = "prosjekter" | "huset" | "direkte";

export interface Channel {
  key: string;
  kind: ChannelKind;
  /** chat_rooms.id eller projects.id */
  id: string;
  title: string;
  unread: number;
  hasMention: boolean;
  lastActivityAt: string | null;
  /** Den andre personen i en direktemelding (avatar i listen). */
  directUserId?: string | null;
  /** Prosjektkoden og fasen til en prosjektkanal (brikke i kanalhodet). */
  projectCode?: string | null;
  projectPhase?: string | null;
}

export interface ChannelGroup {
  id: ChannelGroupId;
  label: string;
  channels: Channel[];
}

export interface RoomInput {
  id: string;
  type: "direct" | "group" | "house";
  name: string | null;
  last_message_at: string | null;
  created_at: string;
  unreadCount: number;
  hasUnreadMention?: boolean;
  otherUser: { user_id: string; full_name: string | null } | null;
}

export interface ProjectChannelInput {
  project_id: string;
  project_code: string | null;
  name: string;
  project_phase?: string | null;
  last_activity_at: string | null;
}

export interface ProjectUnreadInput {
  unread_count: number;
  has_mention: boolean;
}

const GROUP_LABELS: Record<ChannelGroupId, string> = {
  prosjekter: "Prosjekter",
  huset: "Huset",
  direkte: "Direkte",
};

export function channelKey(kind: "room" | "project", id: string): string {
  return `${kind}:${id}`;
}

export function parseChannelKey(key: string | null | undefined): { kind: "room" | "project"; id: string } | null {
  if (!key) return null;
  const [kind, id] = key.split(":");
  if ((kind !== "room" && kind !== "project") || !id) return null;
  return { kind, id };
}

export function projectChannelTitle(code: string | null, name: string): string {
  return code ? `${code} ${name}` : name;
}

export function roomChannel(room: RoomInput): Channel {
  const kind: ChannelKind = room.type;
  const title =
    room.type === "direct"
      ? room.otherUser?.full_name ?? "Direktemelding"
      : room.name ?? (room.type === "house" ? "Huset" : "Gruppe");
  return {
    key: channelKey("room", room.id),
    kind,
    id: room.id,
    title,
    unread: room.unreadCount,
    hasMention: room.unreadCount > 0 && !!room.hasUnreadMention,
    lastActivityAt: room.last_message_at ?? room.created_at,
    directUserId: room.type === "direct" ? room.otherUser?.user_id ?? null : null,
  };
}

export function projectChannel(p: ProjectChannelInput, unread: ProjectUnreadInput | undefined): Channel {
  return {
    key: channelKey("project", p.project_id),
    kind: "project",
    id: p.project_id,
    title: projectChannelTitle(p.project_code, p.name),
    unread: unread?.unread_count ?? 0,
    hasMention: !!unread?.has_mention,
    lastActivityAt: p.last_activity_at,
    projectCode: p.project_code,
    projectPhase: p.project_phase ?? null,
  };
}

/** Nyeste aktivitet først; kanaler uten aktivitet sist, alfabetisk. */
export function sortChannels(channels: Channel[]): Channel[] {
  return [...channels].sort((a, b) => {
    if (a.lastActivityAt && b.lastActivityAt) return b.lastActivityAt.localeCompare(a.lastActivityAt);
    if (a.lastActivityAt) return -1;
    if (b.lastActivityAt) return 1;
    return a.title.localeCompare(b.title, "no");
  });
}

/**
 * Grupperer kanalene i Prosjekter, Huset og Direkte. Grupper og
 * direktemeldinger deler gruppen «Direkte»: begge er samtaler du selv har
 * startet eller blitt lagt til i.
 */
export function buildChannelGroups(channels: Channel[]): ChannelGroup[] {
  const byGroup: Record<ChannelGroupId, Channel[]> = { prosjekter: [], huset: [], direkte: [] };
  for (const c of channels) {
    if (c.kind === "project") byGroup.prosjekter.push(c);
    else if (c.kind === "house") byGroup.huset.push(c);
    else byGroup.direkte.push(c);
  }
  return (Object.keys(byGroup) as ChannelGroupId[]).map((id) => ({
    id,
    label: GROUP_LABELS[id],
    channels: id === "huset" ? [...byGroup[id]].sort((a, b) => a.title.localeCompare(b.title, "no")) : sortChannels(byGroup[id]),
  }));
}

export function totalUnread(channels: Channel[]): number {
  return channels.reduce((sum, c) => sum + c.unread, 0);
}

/** Søk i kanallisten: treff på tittel, uten hensyn til store/små bokstaver. */
export function filterChannels(groups: ChannelGroup[], query: string): ChannelGroup[] {
  const q = query.trim().toLowerCase();
  if (!q) return groups;
  return groups.map((g) => ({ ...g, channels: g.channels.filter((c) => c.title.toLowerCase().includes(q)) }));
}
